import React, { FC, useState, useCallback, useRef } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { errorHandler } from '$store/utils';

interface ImageCropperDialogProps {
  image: string;
  onComplete: (croppedImage: Blob) => void;
  onClose: () => void;
  disableDismissableMask?: boolean;
}

const OUTPUT_SIZE = 256;
const VIEWPORT_SIZE = 600;

const getCroppedImg = async (
  image: HTMLImageElement,
  crop: Crop
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;

  // Calculate the actual displayed size of the image within the square container
  const naturalAspect = image.naturalWidth / image.naturalHeight;
  let displayedWidth = VIEWPORT_SIZE;
  let displayedHeight = VIEWPORT_SIZE;
  let offsetX = 0;
  let offsetY = 0;

  if (naturalAspect > 1) {
    // Wide image - height is smaller, add vertical padding
    displayedHeight = VIEWPORT_SIZE / naturalAspect;
    offsetY = (VIEWPORT_SIZE - displayedHeight) / 2;
  } else if (naturalAspect < 1) {
    // Tall image - width is smaller, add horizontal padding
    displayedWidth = VIEWPORT_SIZE * naturalAspect;
    offsetX = (VIEWPORT_SIZE - displayedWidth) / 2;
  }

  // Convert crop from percentage or pixels to displayed pixels
  let cropX = crop.x ?? 0;
  let cropY = crop.y ?? 0;
  let cropWidth = crop.width ?? 0;
  let cropHeight = crop.height ?? 0;

  if (crop.unit === '%') {
    cropX = (cropX / 100) * VIEWPORT_SIZE;
    cropY = (cropY / 100) * VIEWPORT_SIZE;
    cropWidth = (cropWidth / 100) * VIEWPORT_SIZE;
    cropHeight = (cropHeight / 100) * VIEWPORT_SIZE;
  }

  // Adjust for the offset created by object-fit: contain
  cropX -= offsetX;
  cropY -= offsetY;

  // Clamp to displayed image bounds
  cropX = Math.max(0, Math.min(cropX, displayedWidth));
  cropY = Math.max(0, Math.min(cropY, displayedHeight));
  cropWidth = Math.min(cropWidth, displayedWidth - cropX);
  cropHeight = Math.min(cropHeight, displayedHeight - cropY);

  // Convert to natural image coordinates
  const scaleX = image.naturalWidth / displayedWidth;
  const scaleY = image.naturalHeight / displayedHeight;

  const sourceCropX = cropX * scaleX;
  const sourceCropY = cropY * scaleY;
  const sourceCropWidth = cropWidth * scaleX;
  const sourceCropHeight = cropHeight * scaleY;

  // Draw the cropped image onto the canvas at 256x256
  ctx.drawImage(
    image,
    sourceCropX,
    sourceCropY,
    sourceCropWidth,
    sourceCropHeight,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas is empty'));
        }
      },
      'image/jpeg',
      0.95
    );
  });
};

const ImageCropperDialog: FC<ImageCropperDialogProps> = ({
  image,
  onComplete,
  onClose,
  disableDismissableMask = false,
}) => {
  const [t] = useTranslation('global');
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25,
  });
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);

  const handleImageLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      imageRef.current = event.currentTarget;
    },
    []
  );

  const handleCrop = async () => {
    if (!completedCrop || !imageRef.current) return;

    try {
      const croppedImage = await getCroppedImg(imageRef.current, completedCrop);
      onComplete(croppedImage);
    } catch (error) {
      errorHandler(
        '[ImageCrop]',
        'Something went wrong cropping the image. Try again later',
        true,
        true
      );
    }
  };

  const footer = (
    <div className="flex gap-3 justify-end">
      <Button
        label={t('CompaniesTable.Cancel')}
        icon="pi pi-times"
        onClick={onClose}
        text
        className="text-brand-darkgrey"
      />
      <Button
        label={t('CompaniesTable.ApplyCrop')}
        icon="pi pi-check"
        onClick={handleCrop}
        text
        className="text-brand-darkgrey"
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={t('CompaniesTable.CropImage')}
      visible
      onHide={onClose}
      footer={footer}
      style={{ maxWidth: '90vw' }}
      className="max-h-[90vh]"
      modal
      dismissableMask={!disableDismissableMask}
    >
      <div className="flex flex-col gap-4">
        <div className="flex justify-center">
          <div
            className="rounded-lg"
            style={{
              width: VIEWPORT_SIZE,
              height: VIEWPORT_SIZE,
              backgroundColor: '#FFF',
            }}
          >
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              keepSelection
              ruleOfThirds
            >
              <img
                ref={imageRef}
                src={image}
                alt="Crop target"
                onLoad={handleImageLoad}
                style={{
                  width: VIEWPORT_SIZE,
                  height: VIEWPORT_SIZE,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </ReactCrop>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

ImageCropperDialog.defaultProps = {
  disableDismissableMask: false,
};

export default ImageCropperDialog;
